// Persistent UTM capture for LORS landing pages.
// Captures tracking params on first arrival and keeps them across the
// session so they survive SPA nav, query-string cleanup, or bookmarking.

export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "adset_id",
  "ad_name",
  "ad_id",
] as const;

const STORAGE_KEY = "lors_utms_global";

const readStorage = (): Record<string, string> => {
  if (typeof window === "undefined") return {};
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const writeStorage = (data: Record<string, string>) => {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch { /* ignore */ }
};

const readFromUrl = (): Record<string, string> => {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const data: Record<string, string> = {};
  UTM_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v) data[k] = v;
  });
  return data;
};

/**
 * Call once on landing-page mount. Merges any UTMs in the current URL into
 * sessionStorage so they're available later even if the URL changes.
 */
export const captureUtmsFromUrl = (): void => {
  const fromUrl = readFromUrl();
  if (Object.keys(fromUrl).length === 0) return;
  const stored = readStorage();
  writeStorage({ ...stored, ...fromUrl });
};

/**
 * Call at submit time. Returns the merged set: stored values, then current
 * URL values overlaid on top (URL wins if both present).
 */
export const getUtms = (): Record<string, string> => {
  return { ...readStorage(), ...readFromUrl() };
};
